import api from './api';
import { Collection } from '../types';

/**
 * Collection service for API calls
 * Each collection represents a curated set of products
 */
export const collectionService = {
  /**
   * Get all collections
   * @returns List of collections
   */
  async getAll(): Promise<Collection[]> {
    try {
      return await api.get<Collection[]>('/collections');
    } catch (error) {
      console.error('Error fetching collections:', error);
      throw error;
    }
  },

  /**
   * Get collection by ID
   * @param id - Collection ID
   * @returns Collection data
   */
  async getById(id: number): Promise<Collection> {
    try {
      return await api.get<Collection>(`/collections/${id}`);
    } catch (error) {
      console.error(`Error fetching collection ${id}:`, error);
      throw error;
    }
  },

  /**
   * Create new collection (Admin only)
   * @param collectionData - Collection data
   * @param videoFile - Optional video file
   * @returns Created collection
   */
  async create(collectionData: Record<string, any>, videoFile: File | null = null): Promise<Collection> {
    try {
      const formData = new FormData();

      // Append text fields
      Object.keys(collectionData).forEach(key => {
        if (collectionData[key] !== null && collectionData[key] !== undefined && key !== 'video_url') {
          formData.append(key, collectionData[key]);
        }
      });

      // Append video file if provided
      if (videoFile) {
        formData.append('video', videoFile);
      } else if (collectionData.video_url) {
        // If no file but URL is provided, send the URL
        formData.append('video_url', collectionData.video_url);
      }

      return await api.postFormData<Collection>('/admin/collections', formData);
    } catch (error) {
      console.error('Error creating collection:', error);
      throw error;
    }
  },

  /**
   * Update collection (Admin only)
   * @param id - Collection ID
   * @param collectionData - Updated collection data
   * @param videoFile - Optional video file
   * @returns Updated collection
   */
  async update(id: number, collectionData: Record<string, any>, videoFile: File | null = null): Promise<Collection> {
    try {
      const formData = new FormData();
      formData.append('_method', 'PUT'); // Laravel method spoofing for FormData

      // Append text fields
      Object.keys(collectionData).forEach(key => {
        if (key === 'deleted_video_url' && collectionData[key]) {
          // Handle deleted video URL
          formData.append('deleted_video_url', collectionData[key]);
        } else if (key === 'existing_video_url' && collectionData[key]) {
          // Handle existing video URL
          formData.append('existing_video_url', collectionData[key]);
        } else if (collectionData[key] !== null && collectionData[key] !== undefined && key !== 'video_url') {
          formData.append(key, collectionData[key]);
        }
      });

      // Append video file if provided
      if (videoFile) {
        formData.append('video', videoFile);
      } else if (collectionData.video_url) {
        // If no file but URL is provided, send the URL
        formData.append('video_url', collectionData.video_url);
      }

      return await api.postFormData<Collection>(`/admin/collections/${id}`, formData);
    } catch (error) {
      console.error(`Error updating collection ${id}:`, error);
      throw error;
    }
  },

  /**
   * Delete collection (Admin only)
   * @param id - Collection ID
   */
  async delete(id: number): Promise<void> {
    try {
      return await api.delete<void>(`/admin/collections/${id}`);
    } catch (error) {
      console.error(`Error deleting collection ${id}:`, error);
      throw error;
    }
  },
};
